import React from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';
import { IComparisonFieldSetting, IOfferComparisonFieldValue } from '@models/winnerSelection/ITableDemand';
import { isBestComparisonValue } from '../utils/comparisonFields';

interface IProps {
    field: IComparisonFieldSetting;
    value: IOfferComparisonFieldValue | undefined;
    benchmark: number | null;
    isWithdrawn: boolean;
}

const formatNumber = (value: number, precision: number) =>
    value.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: precision });

const formatMoney = (value: number, currency: string | null, precision: number) => {
    try {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: currency?.trim(),
            minimumFractionDigits: 0,
            maximumFractionDigits: precision,
        }).format(value);
    } catch {
        return `${formatNumber(value, precision)}${currency ? ` ${currency}` : ''}`;
    }
};

const getDelta = (
    field: IComparisonFieldSetting,
    value: IOfferComparisonFieldValue,
    benchmark: number | null,
    isBest: boolean,
) => {
    const current = value.compareValue;
    if (field.comparison === 'none' || current == null || benchmark == null) return '';

    if (field.comparison === 'target') {
        if (value.targetValue == null) return '';
        const difference = current - value.targetValue;
        if (Math.abs(difference) < 10 ** -(field.precision + 2)) return 'соответствует';
        const sign = difference > 0 ? '+' : '−';
        return `${sign}${formatNumber(Math.abs(difference), field.precision)} к требуемому`;
    }

    if (isBest) return 'лучшее значение';
    const difference = field.comparison === 'min' ? current - benchmark : benchmark - current;
    const prefix = field.comparison === 'min' ? '+' : '−';
    const suffix = field.comparison === 'min' ? 'к мин.' : 'от макс.';
    if (field.delta === 'percent' && benchmark !== 0) {
        return `${prefix}${formatNumber((Math.abs(difference) / Math.abs(benchmark)) * 100, 0)}% ${suffix}`;
    }
    return `${prefix}${formatNumber(Math.abs(difference), field.precision)} ${suffix}`;
};

export const ComparisonFieldStat: React.FC<IProps> = ({ field, value, benchmark, isWithdrawn }) => {
    const hasNumericValue = value?.displayValue != null;
    const hasTextValue = value?.textValue != null && value.textValue.trim() !== '';
    const hasValue = field.format === 'text' ? hasTextValue : hasNumericValue;
    const isBest = !isWithdrawn && isBestComparisonValue(field, value?.compareValue, value?.targetValue, benchmark);
    const isExactTarget =
        field.comparison === 'target' &&
        value?.compareValue != null &&
        value.targetValue != null &&
        Math.abs(value.compareValue - value.targetValue) < 10 ** -(field.precision + 2);
    const hasBestDelta = isBest && (field.comparison !== 'target' || isExactTarget);
    const displayValue = !hasValue
        ? '—'
        : field.format === 'money'
          ? formatMoney(value!.displayValue as number, value!.unit, field.precision)
          : field.format === 'number'
            ? formatNumber(value!.displayValue as number, field.precision)
            : value!.textValue;
    const delta = isWithdrawn ? '—' : value ? getDelta(field, value, benchmark, isBest) : '';
    const tooltip = [
        field.hint,
        value?.targetValue != null
            ? `Требуемое значение: ${formatNumber(value.targetValue, field.precision)}${value.unit ? ` ${value.unit}` : ''}`
            : null,
    ]
        .filter(Boolean)
        .join('\n');

    const valueNode = (
        <div className="ws-stat__value" title={field.format === 'text' ? (value?.textValue ?? undefined) : undefined}>
            <span className={clsx({ 'ws-stat__text-value': field.format === 'text' })}>{displayValue}</span>
            {hasValue && field.format === 'number' && value?.unit ? <span className="unit">{value.unit}</span> : null}
        </div>
    );

    return (
        <div className={clsx('ws-stat', { 'ws-stat--best': isBest, 'ws-stat--empty': !hasValue })}>
            <div className="ws-stat__label" title={field.title}>
                {field.title}
            </div>
            {tooltip ? (
                <Tooltip openDelay={100} background="black" position="bottom" label={tooltip}>
                    {valueNode}
                </Tooltip>
            ) : (
                valueNode
            )}
            <div className={clsx('ws-stat__delta', { 'ws-stat__delta--best': hasBestDelta })}>{delta}</div>
        </div>
    );
};
